import { getDatabase, ref, get, update, push, query, orderByChild, equalTo } from 'firebase/database';
import { getStorage, ref as storageRef, uploadBytes, deleteObject, getDownloadURL } from 'firebase/storage';
import {
    KeyEvent,
    KeyEventData,
    KeyEventInput,
    KeyFieldsInput,
    KeyHandOutInput,
    KeyHolder,
    KeyReceipt,
    KeyReceiptKind,
    KeyRegisterData,
    KeyType,
    KeyTypeData,
    KeyTypeAttachmentData,
    KeyTypeInput,
    KEY_HOLDER_KINDS,
    KEY_KINDS,
    MAX_KEYS_PER_BATCH,
    RegisterKey,
    RegisterKeyData,
} from '../types/keyRegister';
import { formatDateForStorage } from '../Helpers/DateUtils';
import { getImageDimensions } from '../Helpers/ImageUploadUtils';
import { DATE_ONLY_RE, todayStockholm } from '../Helpers/leaseUtils';
import {
    effectiveDateRange,
    eventTakesEffectiveDate,
    eventTakesReceipt,
    isAcceptedReceiptFile,
    isAcceptedTypeAttachmentFile,
    isImageFile,
    nextSerialStart,
    nextState,
} from '../Helpers/keyRegisterUtils';
import {
    asRecord,
    optionalString,
    parseKeyEvent,
    parseKeyRegister,
    parseKeyType,
    parseObjectLinks,
    parseRegisterKey,
    parseRegisterKeys,
} from './keyRegisterParser';
import { requireAdmin } from './adminGuard';
import { recordActivity } from './activityApi';
import { DeletedFileTombstone, softDeleteFile } from './fileDeletionApi';

const stripUndefined = <T extends Record<string, unknown>>(value: T): T => {
    const result: Record<string, unknown> = {};
    for (const [key, entry] of Object.entries(value)) {
        if (entry !== undefined) result[key] = entry;
    }
    return result as T;
};

/** Storage file names cannot contain a path separator; keep the rest readable. */
const safeFileName = (name: string, fallback: string): string => {
    const cleaned = name.replace(/[/\\#?[\]*]/g, '_').trim();
    return cleaned.length > 0 ? cleaned : fallback;
};

/** Tombstone entity type of a key type's file. */
export const KEY_TYPE_ATTACHMENT_ENTITY_TYPE = 'keyTypeAttachment';

/** Tombstone entity type of a Låsschema file. */
export const LOCK_SCHEDULE_ENTITY_TYPE = 'lockScheduleAttachment';

const LOCK_SCHEDULES_PATH = 'keyRegister/lockSchedules';

/**
 * Nyckelregister at `keyRegister/` (spec docs/specs/key-register.md):
 * `types/{typeKey}` and `keys/{keyKey}` with an append-only `log`. Admin-only
 * end to end. Every event is one root `update()` writing the log entry and the
 * key's status/holder/dueDate/updatedBy/updatedWhen; the transition is checked
 * here through `nextState` (the rules only enforce write-once log entries).
 * Keys are never deleted; `scrapped` is terminal.
 */
export class KeyRegisterApi {
    private db = getDatabase();

    typePath(typeKey: string): string {
        return `keyRegister/types/${typeKey}`;
    }

    keyPath(keyKey: string): string {
        return `keyRegister/keys/${keyKey}`;
    }

    receiptStoragePath(keyKey: string, eventKey: string, fileName: string): string {
        return `keyRegister/${keyKey}/${eventKey}/${safeFileName(fileName, 'kvitto')}`;
    }

    typeAttachmentPath(typeKey: string, attachmentKey: string): string {
        return `${this.typePath(typeKey)}/attachments/${attachmentKey}`;
    }

    /** The DB path plus the file name, so a type's files sit under one Storage prefix. */
    typeAttachmentStoragePath(typeKey: string, attachmentKey: string, fileName: string): string {
        return `${this.typeAttachmentPath(typeKey, attachmentKey)}/${safeFileName(fileName, 'fil')}`;
    }

    lockSchedulePath(fileKey: string): string {
        return `${LOCK_SCHEDULES_PATH}/${fileKey}`;
    }

    lockScheduleStoragePath(fileKey: string, fileName: string): string {
        return `${this.lockSchedulePath(fileKey)}/${safeFileName(fileName, 'fil')}`;
    }

    private newKey(path: string, what: string): string {
        const key = push(ref(this.db, path)).key;
        if (!key) throw new Error(`Kunde inte skapa nyckel för ${what}`);
        return key;
    }

    // MARK: - Parsing (pure, in keyRegisterParser.ts)

    parseType(data: unknown, key: string): KeyType | null {
        return parseKeyType(data, key);
    }

    parseEvent(data: unknown, key: string): KeyEvent | null {
        return parseKeyEvent(data, key);
    }

    parseKey(data: unknown, key: string): RegisterKey | null {
        return parseRegisterKey(data, key);
    }

    parseRegister(data: unknown): KeyRegisterData {
        return parseKeyRegister(data);
    }

    // MARK: - Reads

    /** The whole (small, admin-only) root: every type, including archived ones, and every key. */
    async getAll(): Promise<KeyRegisterData> {
        const snapshot = await get(ref(this.db, 'keyRegister'));
        return this.parseRegister(snapshot.exists() ? snapshot.val() : null);
    }

    private async requireType(typeKey: string): Promise<KeyType> {
        const snapshot = await get(ref(this.db, this.typePath(typeKey)));
        const type = snapshot.exists() ? this.parseType(snapshot.val(), typeKey) : null;
        if (!type) throw new Error('Nyckeltypen hittades inte');
        return type;
    }

    private async requireKey(keyKey: string): Promise<RegisterKey> {
        const snapshot = await get(ref(this.db, this.keyPath(keyKey)));
        const key = snapshot.exists() ? this.parseKey(snapshot.val(), keyKey) : null;
        if (!key) throw new Error('Nyckeln hittades inte');
        return key;
    }

    /** The raw key nodes of one type (`.indexOn: typeKey`); parsed, so malformed nodes drop out. */
    private async keysOfType(typeKey: string): Promise<RegisterKey[]> {
        const snapshot = await get(query(ref(this.db, 'keyRegister/keys'), orderByChild('typeKey'), equalTo(typeKey)));
        return snapshot.exists() ? parseRegisterKeys(snapshot.val()) : [];
    }

    async getReceiptUrl(storagePath: string): Promise<string> {
        return getDownloadURL(storageRef(getStorage(), storagePath));
    }

    // MARK: - Types

    private validateTypeInput(input: KeyTypeInput): Omit<KeyTypeData, 'createdBy' | 'createdWhen' | 'archived'> {
        const name = optionalString(input.name);
        if (!name) throw new Error('Namn krävs');
        if (!KEY_KINDS.includes(input.kind)) throw new Error('Ogiltig nyckeltyp');
        const opensBuildings: { [buildingKey: string]: true } = {};
        for (const buildingKey of input.opensBuildings ?? []) {
            const k = optionalString(buildingKey);
            if (k) opensBuildings[k] = true;
        }
        return {
            name,
            kind: input.kind,
            description: optionalString(input.description),
            lockSystemRef: optionalString(input.lockSystemRef),
            opensBuildings: Object.keys(opensBuildings).length > 0 ? opensBuildings : undefined,
            opensUnits: parseObjectLinks(input.opensUnits),
            opensStorageUnits: parseObjectLinks(input.opensStorageUnits),
            opens: optionalString(input.opens),
        };
    }

    async createType(input: KeyTypeInput): Promise<KeyType> {
        const uid = await requireAdmin(this.db, 'skapa nyckeltyper');
        const validated = this.validateTypeInput(input);
        const key = this.newKey('keyRegister/types', 'nyckeltypen');
        const node: KeyTypeData = stripUndefined({
            ...validated,
            createdBy: uid,
            createdWhen: formatDateForStorage(new Date()),
        });
        await update(ref(this.db), { [this.typePath(key)]: node });
        recordActivity('keyType.created', { targetKey: key, label: node.name });
        return { key, ...node };
    }

    async updateType(typeKey: string, input: KeyTypeInput): Promise<void> {
        const uid = await requireAdmin(this.db, 'ändra nyckeltyper');
        const validated = this.validateTypeInput(input);
        const existing = await this.requireType(typeKey);
        if (existing.kind === 'tag' && validated.kind === 'mechanical') {
            const keys = await this.keysOfType(typeKey);
            if (keys.some(key => key.status === 'blocked')) {
                throw new Error('Typen har spärrade taggar och kan inte bli mekanisk');
            }
        }
        const path = this.typePath(typeKey);
        await update(ref(this.db), {
            [`${path}/name`]: validated.name,
            [`${path}/kind`]: validated.kind,
            [`${path}/description`]: validated.description ?? null,
            [`${path}/lockSystemRef`]: validated.lockSystemRef ?? null,
            [`${path}/opensBuildings`]: validated.opensBuildings ?? null,
            [`${path}/opensUnits`]: validated.opensUnits ?? null,
            [`${path}/opensStorageUnits`]: validated.opensStorageUnits ?? null,
            [`${path}/opens`]: validated.opens ?? null,
            [`${path}/updatedBy`]: uid,
            [`${path}/updatedWhen`]: formatDateForStorage(new Date()),
        });
        recordActivity('keyType.updated', { targetKey: typeKey, label: validated.name });
    }

    async setTypeArchived(typeKey: string, archived: boolean): Promise<void> {
        const uid = await requireAdmin(this.db, archived ? 'arkivera nyckeltyper' : 'återställa nyckeltyper');
        const type = await this.requireType(typeKey);
        const path = this.typePath(typeKey);
        await update(ref(this.db), {
            [`${path}/archived`]: archived ? true : null,
            [`${path}/updatedBy`]: uid,
            [`${path}/updatedWhen`]: formatDateForStorage(new Date()),
        });
        recordActivity(archived ? 'keyType.archived' : 'keyType.updated', { targetKey: typeKey, label: type.name });
    }

    archiveType(typeKey: string): Promise<void> {
        return this.setTypeArchived(typeKey, true);
    }

    unarchiveType(typeKey: string): Promise<void> {
        return this.setTypeArchived(typeKey, false);
    }

    /**
     * Hard delete, only for a type without keys (any status, scrapped included).
     * Its files are tombstoned in the same update.
     */
    async deleteType(typeKey: string): Promise<void> {
        const uid = await requireAdmin(this.db, 'ta bort nyckeltyper');
        const type = await this.requireType(typeKey);
        const snapshot = await get(query(ref(this.db, 'keyRegister/keys'), orderByChild('typeKey'), equalTo(typeKey)));
        if (snapshot.exists() && Object.keys(asRecord(snapshot.val())).length > 0) {
            throw new Error('Nyckeltypen har nycklar och kan inte tas bort. Arkivera den i stället.');
        }
        const updates: Record<string, unknown> = { [this.typePath(typeKey)]: null };
        const deletedAt = formatDateForStorage(new Date());
        for (const [attachmentKey, metadata] of Object.entries(type.attachments ?? {})) {
            const tombstone: DeletedFileTombstone = {
                originalPath: this.typeAttachmentPath(typeKey, attachmentKey),
                entityType: KEY_TYPE_ATTACHMENT_ENTITY_TYPE,
                storagePath: metadata.storagePath,
                metadata,
                deletedBy: uid,
                deletedAt,
            };
            updates[`deletedFiles/${attachmentKey}`] = tombstone;
        }
        await update(ref(this.db), updates);
        recordActivity('keyType.deleted', { targetKey: typeKey, label: type.name });
    }

    // MARK: - Type attachments

    /**
     * Uploads each file to its own write-once path and writes its node (plus the
     * type's updatedBy/updatedWhen) right after, so a failure part-way keeps the
     * files already saved. Images carry their pixel size; other files 0 × 0.
     */
    async addTypeAttachments(typeKey: string, files: File[]): Promise<void> {
        const uid = await requireAdmin(this.db, 'lägga till filer på nyckeltyper');
        this.requireAcceptedFiles(files);
        await this.requireType(typeKey);
        const path = this.typePath(typeKey);
        for (const file of files) {
            const attachmentKey = this.newKey(`${path}/attachments`, 'filen');
            const node = await this.uploadFile(file, this.typeAttachmentStoragePath(typeKey, attachmentKey, file.name), uid);
            await update(ref(this.db), {
                [this.typeAttachmentPath(typeKey, attachmentKey)]: node,
                [`${path}/updatedBy`]: uid,
                [`${path}/updatedWhen`]: node.date,
            });
            recordActivity('document.uploaded', { targetKey: attachmentKey });
        }
    }

    /** Soft delete: the node moves to `deletedFiles/{attachmentKey}`, the blob stays. */
    async removeTypeAttachment(typeKey: string, attachmentKey: string): Promise<void> {
        const uid = await requireAdmin(this.db, 'ta bort filer från nyckeltyper');
        const path = this.typePath(typeKey);
        await softDeleteFile({
            originalPath: this.typeAttachmentPath(typeKey, attachmentKey),
            entityType: KEY_TYPE_ATTACHMENT_ENTITY_TYPE,
            extraUpdates: {
                [`${path}/updatedBy`]: uid,
                [`${path}/updatedWhen`]: formatDateForStorage(new Date()),
            },
        });
    }

    async getTypeAttachmentUrl(storagePath: string): Promise<string> {
        return getDownloadURL(storageRef(getStorage(), storagePath));
    }

    // MARK: - Låsschema (register-wide files)

    /** Like `addTypeAttachments`: one write-once upload and one node per file, so earlier files stay on a failure. */
    async addLockSchedules(files: File[]): Promise<void> {
        const uid = await requireAdmin(this.db, 'lägga till låsscheman');
        this.requireAcceptedFiles(files);
        for (const file of files) {
            const fileKey = this.newKey(LOCK_SCHEDULES_PATH, 'filen');
            const node = await this.uploadFile(file, this.lockScheduleStoragePath(fileKey, file.name), uid);
            await update(ref(this.db), { [this.lockSchedulePath(fileKey)]: node });
            recordActivity('document.uploaded', { targetKey: fileKey });
        }
    }

    /** Soft delete: the node moves to `deletedFiles/{fileKey}`, the blob stays. */
    async removeLockSchedule(fileKey: string): Promise<void> {
        await requireAdmin(this.db, 'ta bort låsscheman');
        await softDeleteFile({
            originalPath: this.lockSchedulePath(fileKey),
            entityType: LOCK_SCHEDULE_ENTITY_TYPE,
        });
    }

    private requireAcceptedFiles(files: File[]): void {
        const rejected = files.find(file => !isAcceptedTypeAttachmentFile(file));
        if (rejected) throw new Error(`${rejected.name}: endast PDF, Word, Excel eller bilder är tillåtna`);
    }

    /** Uploads one file to its write-once path and returns its node. Images carry their pixel size; other files 0 × 0. */
    private async uploadFile(file: File, storagePath: string, uid: string): Promise<KeyTypeAttachmentData> {
        let width = 0;
        let height = 0;
        if (isImageFile(file)) {
            try {
                ({ width, height } = await getImageDimensions(file));
            } catch {
                // HEIC and other formats the browser cannot decode keep 0 × 0.
            }
        }
        try {
            await uploadBytes(storageRef(getStorage(), storagePath), file, { contentType: file.type || undefined });
        } catch (err) {
            console.error('Key register file upload failed:', err);
            throw new Error(`Kunde inte ladda upp ${file.name}`);
        }
        return {
            fileName: file.name,
            mimeType: file.type,
            storagePath,
            author: uid,
            date: formatDateForStorage(new Date()),
            width,
            height,
            fileSize: file.size,
        };
    }

    // MARK: - Keys

    /**
     * Creates `count` keys of the type in one root update, each with its
     * `created` event embedded in the node (ancestor + descendant paths cannot
     * share an update). Serials run from `startNumber`, default the type's
     * highest numeric serial + 1.
     */
    async addKeys(typeKey: string, count: number, startNumber?: number): Promise<RegisterKey[]> {
        const uid = await requireAdmin(this.db, 'lägga till nycklar');
        if (!Number.isInteger(count) || count < 1) throw new Error('Antal måste vara minst 1');
        if (count > MAX_KEYS_PER_BATCH) throw new Error(`Högst ${MAX_KEYS_PER_BATCH} nycklar åt gången`);
        if (startNumber !== undefined && (!Number.isInteger(startNumber) || startNumber < 0)) {
            throw new Error('Startnumret måste vara ett heltal');
        }
        const type = await this.requireType(typeKey);
        if (type.archived) throw new Error('Nyckeltypen är arkiverad');
        const start = startNumber ?? nextSerialStart(await this.keysOfType(typeKey));
        const when = formatDateForStorage(new Date());
        const updates: Record<string, unknown> = {};
        const created: RegisterKey[] = [];
        for (let i = 0; i < count; i += 1) {
            const key = this.newKey('keyRegister/keys', 'nyckeln');
            const eventKey = this.newKey(`${this.keyPath(key)}/log`, 'logghändelsen');
            const event: KeyEventData = { type: 'created', actor: uid, when, effectiveDate: when.slice(0, 10) };
            const node: RegisterKeyData = {
                typeKey,
                serial: String(start + i),
                status: 'inStock',
                createdBy: uid,
                createdWhen: when,
            };
            updates[this.keyPath(key)] = { ...node, log: { [eventKey]: event } };
            created.push({ ...node, key, log: [{ key: eventKey, ...event }] });
        }
        await update(ref(this.db), updates);
        recordActivity('key.created', { targetKey: created.length === 1 ? created[0].key : undefined, label: type.name });
        return created;
    }

    /** Serial and location are plain fields, not logged. */
    async updateKeyFields(keyKey: string, input: KeyFieldsInput): Promise<void> {
        const uid = await requireAdmin(this.db, 'ändra nycklar');
        await this.requireKey(keyKey);
        const path = this.keyPath(keyKey);
        await update(ref(this.db), {
            [`${path}/serial`]: optionalString(input.serial) ?? null,
            [`${path}/location`]: optionalString(input.location) ?? null,
            [`${path}/updatedBy`]: uid,
            [`${path}/updatedWhen`]: formatDateForStorage(new Date()),
        });
        recordActivity('key.updated', { targetKey: keyKey });
    }

    // MARK: - Events

    /** A stale pick gets a readable message rather than a dangling holder. External users live behind a callable and are not checked. */
    private async assertHolderExists(holder: KeyHolder): Promise<void> {
        if (holder.kind === 'tenant') {
            const snapshot = await get(ref(this.db, `tenants/${holder.key}`));
            if (!snapshot.exists()) throw new Error('Personen finns inte längre');
            if (asRecord(snapshot.val()).anonymized === true) throw new Error('Personen är borttagen');
        } else if (holder.kind === 'user') {
            const snapshot = await get(ref(this.db, `users/userList/${holder.key}`));
            if (!snapshot.exists()) throw new Error('Användaren finns inte längre');
        }
    }

    private async uploadReceipt(
        keyKey: string,
        eventKey: string,
        file: File,
        kind: KeyReceiptKind,
        uid: string,
    ): Promise<KeyReceipt> {
        if (!isAcceptedReceiptFile(file)) throw new Error('Kvittot måste vara en bild eller en PDF');
        let width = 0;
        let height = 0;
        if (isImageFile(file)) {
            try {
                ({ width, height } = await getImageDimensions(file));
            } catch {
                throw new Error('Kunde inte läsa bilden');
            }
        }
        const storagePath = this.receiptStoragePath(keyKey, eventKey, file.name);
        try {
            await uploadBytes(storageRef(getStorage(), storagePath), file, { contentType: file.type || undefined });
        } catch (err) {
            console.error('Receipt upload failed:', err);
            throw new Error('Kunde inte ladda upp kvittot');
        }
        return {
            kind,
            storagePath,
            author: uid,
            date: formatDateForStorage(new Date()),
            width,
            height,
            fileSize: file.size,
        };
    }

    /**
     * Records one event: validates the transition, uploads the receipt (if
     * any) to `keyRegister/{keyKey}/{eventKey}/{fileName}`, then writes the log
     * entry and the key's new state in one root update. If that write fails, a
     * best-effort rollback of the blob is attempted (the storage rules refuse
     * it today). `returned`/`lost`/`blocked`/`scrapped` copy the key's
     * current holder to the event so the history keeps who had it.
     * A `comment` (note required) writes only the log entry and updatedBy/updatedWhen.
     * Every event carries `effectiveDate`: today, or for a hand-out or return an earlier day within
     * `effectiveDateRange`; a due date may not be before it. `when` stays the time it was recorded.
     */
    async recordEvent(keyKey: string, input: KeyEventInput): Promise<KeyEvent> {
        const uid = await requireAdmin(this.db, 'registrera nyckelhändelser');
        const key = await this.requireKey(keyKey);
        const type = await this.requireType(key.typeKey);
        const note = optionalString(input.note);
        if (input.type === 'comment' && !note) throw new Error('En kommentar måste ha en text');
        const dueDate = input.type === 'handedOut' ? optionalString(input.dueDate) : undefined;
        if (input.type !== 'handedOut' && optionalString(input.dueDate)) throw new Error('Återlämningsdatum gäller bara utlämning');
        const today = todayStockholm();
        const pickedDate = optionalString(input.effectiveDate);
        if (pickedDate && !eventTakesEffectiveDate(input.type)) throw new Error('Datum kan bara väljas vid utlämning och återlämning');
        if (pickedDate && !DATE_ONLY_RE.test(pickedDate)) throw new Error('Ogiltigt datum');
        const effectiveDate = pickedDate ?? today;
        if (eventTakesEffectiveDate(input.type)) {
            const range = effectiveDateRange(key, today);
            if (effectiveDate > range.max) throw new Error('Datumet kan inte vara i framtiden');
            if (range.min && effectiveDate < range.min) throw new Error(`Datumet kan inte vara före nyckelns förra händelse (${range.min})`);
        }
        if (dueDate && DATE_ONLY_RE.test(dueDate) && dueDate < effectiveDate) throw new Error('Åter senast kan inte vara före utlämningsdatumet');
        if (input.receiptFile && !eventTakesReceipt(input.type)) {
            throw new Error('Kvitto kan bara bifogas vid utlämning och återlämning');
        }
        const holderInput = input.type === 'handedOut' && input.holder
            ? { kind: input.holder.kind, key: optionalString(input.holder.key) ?? '' }
            : undefined;
        if (holderInput && !KEY_HOLDER_KINDS.includes(holderInput.kind)) throw new Error('Ogiltig mottagare');
        const state = nextState(key, type.kind, { type: input.type, holder: holderInput, dueDate });
        if (input.type === 'handedOut' && state.holder) await this.assertHolderExists(state.holder);

        // A comment names no holder, so it never counts as a reference to one (tenant deletion, holder lookups).
        const eventHolder = input.type === 'handedOut' ? state.holder : input.type === 'comment' ? undefined : key.holder;
        const path = this.keyPath(keyKey);
        const eventKey = this.newKey(`${path}/log`, 'logghändelsen');
        const receipt = input.receiptFile
            ? await this.uploadReceipt(keyKey, eventKey, input.receiptFile, input.receiptKind ?? 'scan', uid)
            : undefined;
        const event: KeyEventData = stripUndefined({
            type: input.type,
            actor: uid,
            when: formatDateForStorage(new Date()),
            effectiveDate,
            note,
            holder: eventHolder,
            dueDate: input.type === 'handedOut' ? state.dueDate : undefined,
            receipt,
        });
        try {
            await update(ref(this.db), {
                [`${path}/log/${eventKey}`]: event,
                // A comment leaves status/holder/dueDate untouched (not rewritten).
                ...(input.type === 'comment' ? {} : {
                    [`${path}/status`]: state.status,
                    [`${path}/holder`]: state.holder ?? null,
                    [`${path}/dueDate`]: state.dueDate ?? null,
                }),
                [`${path}/updatedBy`]: uid,
                [`${path}/updatedWhen`]: event.when,
            });
        } catch (err) {
            // Best effort: storage.rules deny client deletes, so this is refused today and the blob stays
            // (unreferenced). Kept as-is per web CLAUDE.md (upload-rollback deleteObject calls stay).
            if (receipt) {
                try {
                    await deleteObject(storageRef(getStorage(), receipt.storagePath));
                } catch (cleanupError) {
                    console.warn('Failed to roll back receipt upload:', cleanupError);
                }
            }
            console.error('Key event write failed:', err);
            throw new Error('Kunde inte spara händelsen');
        }
        const action = input.type === 'handedOut' ? 'key.handedOut' : input.type === 'returned' ? 'key.returned' : 'key.updated';
        recordActivity(action, { targetKey: keyKey, label: type.name });
        return { key: eventKey, ...event };
    }

    /**
     * "Lämna ut nycklar": hands out several in-stock keys to one holder with the same date, due date,
     * note and receipt (uploaded once per key, as each event owns its blob). Every key is checked as in
     * `recordEvent` before anything is written; the date must fit every key's `effectiveDateRange`.
     * All events and key states go in one root update, so either every key is handed out or none.
     * Archived types are not refused here, as in `recordEvent`; the picker leaves them out.
     */
    async handOutKeys(keyKeys: string[], input: KeyHandOutInput): Promise<KeyEvent[]> {
        const uid = await requireAdmin(this.db, 'lämna ut nycklar');
        const distinct = Array.from(new Set(keyKeys));
        if (distinct.length === 0) throw new Error('Välj minst en nyckel');
        if (distinct.length > MAX_KEYS_PER_BATCH) throw new Error(`Högst ${MAX_KEYS_PER_BATCH} nycklar åt gången`);
        const note = optionalString(input.note);
        const dueDate = optionalString(input.dueDate);
        const today = todayStockholm();
        const pickedDate = optionalString(input.effectiveDate);
        if (pickedDate && !DATE_ONLY_RE.test(pickedDate)) throw new Error('Ogiltigt datum');
        const effectiveDate = pickedDate ?? today;
        if (effectiveDate > today) throw new Error('Datumet kan inte vara i framtiden');
        if (dueDate && DATE_ONLY_RE.test(dueDate) && dueDate < effectiveDate) throw new Error('Åter senast kan inte vara före utlämningsdatumet');
        if (input.receiptFile && !isAcceptedReceiptFile(input.receiptFile)) throw new Error('Kvittot måste vara en bild eller en PDF');
        const holderInput: KeyHolder = { kind: input.holder?.kind, key: optionalString(input.holder?.key) ?? '' };
        if (!KEY_HOLDER_KINDS.includes(holderInput.kind)) throw new Error('Ogiltig mottagare');

        const planned: Array<{ keyKey: string; typeName: string; state: ReturnType<typeof nextState> }> = [];
        for (const keyKey of distinct) {
            const key = await this.requireKey(keyKey);
            const type = await this.requireType(key.typeKey);
            const label = `${type.name}, ${key.serial ? `nr ${key.serial}` : 'utan nummer'}`;
            if (key.status !== 'inStock') throw new Error(`${label} är inte inne`);
            const { min } = effectiveDateRange(key, today);
            if (min && effectiveDate < min) throw new Error(`Datumet kan inte vara före förra händelsen för ${label} (${min})`);
            planned.push({ keyKey, typeName: type.name, state: nextState(key, type.kind, { type: 'handedOut', holder: holderInput, dueDate }) });
        }
        await this.assertHolderExists(holderInput);

        const when = formatDateForStorage(new Date());
        const updates: Record<string, unknown> = {};
        const events: KeyEvent[] = [];
        const receipts: KeyReceipt[] = [];
        let writing = false;
        try {
            for (const { keyKey, state } of planned) {
                const path = this.keyPath(keyKey);
                const eventKey = this.newKey(`${path}/log`, 'logghändelsen');
                const receipt = input.receiptFile
                    ? await this.uploadReceipt(keyKey, eventKey, input.receiptFile, input.receiptKind ?? 'scan', uid)
                    : undefined;
                if (receipt) receipts.push(receipt);
                const event: KeyEventData = stripUndefined({
                    type: 'handedOut' as const,
                    actor: uid,
                    when,
                    effectiveDate,
                    note,
                    holder: state.holder,
                    dueDate: state.dueDate,
                    receipt,
                });
                updates[`${path}/log/${eventKey}`] = event;
                updates[`${path}/status`] = state.status;
                updates[`${path}/holder`] = state.holder ?? null;
                updates[`${path}/dueDate`] = state.dueDate ?? null;
                updates[`${path}/updatedBy`] = uid;
                updates[`${path}/updatedWhen`] = when;
                events.push({ key: eventKey, ...event });
            }
            writing = true;
            await update(ref(this.db), updates);
        } catch (err) {
            // Best effort, as in recordEvent: storage.rules deny client deletes, so this is refused today.
            for (const receipt of receipts) {
                try {
                    await deleteObject(storageRef(getStorage(), receipt.storagePath));
                } catch (cleanupError) {
                    console.warn('Failed to roll back receipt upload:', cleanupError);
                }
            }
            console.error('Key hand-out write failed:', err);
            // A failed upload keeps its own message (uploadReceipt's); a failed write gets the general one.
            throw writing || !(err instanceof Error) ? new Error('Kunde inte lämna ut nycklarna') : err;
        }
        for (const { keyKey, typeName } of planned) recordActivity('key.handedOut', { targetKey: keyKey, label: typeName });
        return events;
    }
}

export const keyRegisterApi = new KeyRegisterApi();
